/**
 * Drawer — slide-in left panel with two tabs: Chats and Agents.
 *
 *   Chats tab  — live chat list driven by atmosStore.convs.
 *   Agents tab — live agent roster (atmosStore.agents) + Working/
 *                Completed activity cards (atmosStore.runs) +
 *                per-agent options menu + "+ New agent" CTA.
 *
 * Pair gate: when the link is not "paired" the PairWall covers the
 * shell so this drawer's empty states are only seen post-pair.
 */

import { Avatar } from "./Avatar";
import { Icon } from "./Icon";
import { atmosStore } from "../lib/atmosStore";
import type { AtmosConv, AtmosAgent, AtmosRun } from "../lib/atmosWS";
import { atmosWS } from "../lib/atmosWS";
import { AgentOptionsMenu } from "./AgentOptionsMenu";

// Local alias kept for the AgentOptionsMenu callback shape.
export type Agent = AtmosAgent & { tagline: string };

type Tab = "chats" | "agents";
type RunFilter = "working" | "completed";

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function buildItem(
  id: string,
  name: string,
  iconKey: string | undefined,
  secondary: string,
  onPick: (id: string) => void,
  unread?: number,
): HTMLElement {
  const btn = el("button", "drawer__item");
  btn.type = "button";
  btn.setAttribute("data-id", id);

  const av = el("div", "drawer__item-avatar");
  av.appendChild(Avatar({ name, iconKey, sizePx: 36 }));
  btn.appendChild(av);

  const text = el("div", "drawer__item-text");
  text.appendChild(el("div", "drawer__item-name", name));
  text.appendChild(el("div", "drawer__item-sub", secondary));
  btn.appendChild(text);

  if (unread && unread > 0) {
    const pill = el("div", "drawer__item-unread", unread > 99 ? "99+" : String(unread));
    btn.appendChild(pill);
  }

  btn.addEventListener("click", () => onPick(id));
  return btn;
}

interface AgentRowHandlers {
  onPick: (id: string) => void;
  onConfigure: (a: Agent) => void;
  onRenameSubmit: (id: string, newName: string) => void;
  onDuplicate: (a: Agent) => void;
  onDelete: (a: Agent) => void;
}

function buildAgentRow(a: Agent, h: AgentRowHandlers): HTMLElement {
  const row = el("div", "drawer__agent-row");
  row.setAttribute("data-agent-id", a.id);

  const main = buildItem(a.id, a.name, a.iconKey, a.tagline, h.onPick);
  main.classList.add("drawer__agent-main");
  row.appendChild(main);

  const opts = el("button", "drawer__agent-opts");
  opts.type = "button";
  opts.setAttribute("aria-label", `${a.name} options`);
  opts.appendChild(Icon("more", 16));
  opts.addEventListener("click", (e) => {
    e.stopPropagation();
    const menu = AgentOptionsMenu({
      anchor: opts,
      agentId: a.id,
      agentName: a.name,
      onConfigure: () => h.onConfigure(a),
      onRename: () => startInlineRename(row, a, h.onRenameSubmit),
      onDuplicate: () => h.onDuplicate(a),
      onDelete: () => h.onDelete(a),
    });
    document.body.appendChild(menu);
  });
  row.appendChild(opts);

  return row;
}

function startInlineRename(
  row: HTMLElement,
  a: Agent,
  onSubmit: (id: string, newName: string) => void,
): void {
  const nameEl = row.querySelector<HTMLElement>(".drawer__item-name");
  if (!nameEl) return;
  const input = document.createElement("input");
  input.className = "drawer__agent-rename";
  input.value = a.name;
  input.maxLength = 32;
  input.setAttribute("aria-label", "Rename agent");
  nameEl.replaceWith(input);
  setTimeout(() => { input.focus(); input.select(); }, 0);

  function commit(save: boolean): void {
    const v = input.value.trim();
    const fresh = document.createElement("div");
    fresh.className = "drawer__item-name";
    fresh.textContent = save && v ? v : a.name;
    input.replaceWith(fresh);
    if (save && v && v !== a.name) onSubmit(a.id, v);
  }

  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter") { e.preventDefault(); commit(true); }
    else if (e.key === "Escape") { e.preventDefault(); commit(false); }
  });
  input.addEventListener("blur", () => commit(true));
  input.addEventListener("click", (e) => e.stopPropagation());
}

function relativeTime(tsSec: string): string {
  const ts = Number.parseInt(tsSec, 10);
  if (!Number.isFinite(ts)) return "";
  const diff = Math.max(0, Math.floor(Date.now() / 1000) - ts);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function buildStep(step: { label: string; done?: boolean; active?: boolean }): HTMLElement {
  const row = el("li", "drawer__run-step");
  const glyph = el("span", "drawer__run-step-glyph");
  if (step.done) {
    glyph.classList.add("drawer__run-step-glyph--done");
    glyph.appendChild(Icon("check", 12));
  } else if (step.active) {
    glyph.classList.add("drawer__run-step-glyph--active");
  } else {
    glyph.classList.add("drawer__run-step-glyph--pending");
  }
  row.appendChild(glyph);
  row.appendChild(el("span", "drawer__run-step-label", step.label));
  return row;
}

function buildRunCard(run: AtmosRun): HTMLElement {
  const card = el("article", "drawer__run");
  card.setAttribute("data-status", run.status);

  const head = el("div", "drawer__run-head");
  const av = el("div", "drawer__run-av");
  av.appendChild(Avatar({ name: run.agentName, iconKey: run.agentIconKey, sizePx: 24 }));
  head.appendChild(av);

  const meta = el("div", "drawer__run-meta");
  meta.appendChild(el("div", "drawer__run-agent", run.agentName));
  meta.appendChild(el("div", "drawer__run-time", relativeTime(run.startedAt)));
  head.appendChild(meta);

  const badge = el("span", `drawer__run-badge drawer__run-badge--${run.status}`);
  if (run.status === "working") {
    badge.appendChild(Icon("activity", 11));
    badge.appendChild(document.createTextNode(" Running"));
  } else if (run.status === "done") {
    badge.appendChild(Icon("check", 11));
    badge.appendChild(document.createTextNode(" Done"));
  } else if (run.status === "failed") {
    badge.appendChild(Icon("close", 11));
    badge.appendChild(document.createTextNode(" Failed"));
  } else {
    badge.appendChild(Icon("clock", 11));
    badge.appendChild(document.createTextNode(" Queued"));
  }
  head.appendChild(badge);
  card.appendChild(head);

  card.appendChild(el("h4", "drawer__run-title", run.title));

  const bar = el("div", "drawer__run-bar");
  const fill = el("div", "drawer__run-bar-fill");
  fill.style.width = `${Math.round(run.progress * 100)}%`;
  bar.appendChild(fill);
  card.appendChild(bar);

  const steps = el("ul", "drawer__run-steps");
  for (const s of run.steps) steps.appendChild(buildStep(s));
  card.appendChild(steps);

  return card;
}

function emptyBlock(title: string, sub: string): HTMLElement {
  const wrap = el("div", "drawer__empty-state");
  const t = el("div", "drawer__empty-title", title);
  const s = el("div", "drawer__empty-sub", sub);
  wrap.append(t, s);
  return wrap;
}

export interface DrawerOpts {
  onPick: (id: string) => void;
  onCreate: (kind: "chat" | "agent") => void;
  onAgentConfigure: (a: Agent) => void;
  getActiveId: () => string;
  /** When set, the drawer shows ONLY that pane and hides the tab bar.
   *  Used to give Chats its own left drawer and Agents its own right
   *  drawer, matching the wider 3-pane nav from the design memory. */
  singleTab?: Tab;
  /** Slide side. Defaults to "left" for backward-compat. */
  side?: "left" | "right";
}

export function Drawer(opts: DrawerOpts): HTMLElement {
  const { onPick, onCreate, onAgentConfigure, getActiveId, singleTab, side = "left" } = opts;
  const root = el("aside", `drawer drawer--${side}`);
  root.setAttribute("aria-label", side === "right" ? "Agents" : "Chats");

  let tab: Tab = singleTab ?? "chats";
  let filter: RunFilter = "working";
  // Optimistic agent overlays so the UI updates instantly when the
  // user renames/deletes from the ⋮ popover, even before Mac echoes
  // back the agent.list.r broadcast.
  const renamedAgents = new Map<string, string>();
  const deletedAgents = new Set<string>();

  function syncActive(): void {
    const active = getActiveId();
    root.querySelectorAll<HTMLElement>(".drawer__item").forEach((btn) => {
      const on = btn.getAttribute("data-id") === active;
      btn.classList.toggle("drawer__item--active", on);
    });
  }

  // Pane title — only shown in single-tab mode (each side has ONE job).
  if (singleTab) {
    const ttl = el("div", "drawer__pane-title", singleTab === "chats" ? "Chats" : "Agents");
    root.appendChild(ttl);
  }

  const cta = el("button", "drawer__new");
  cta.type = "button";
  const ctaIcon = el("span", "drawer__new-icon");
  ctaIcon.appendChild(Icon("newChat", 18));
  const ctaLabel = el("span", undefined, tab === "chats" ? "New chat" : "New agent");
  cta.appendChild(ctaIcon);
  cta.appendChild(ctaLabel);
  cta.addEventListener("click", () => onCreate(tab === "chats" ? "chat" : "agent"));
  root.appendChild(cta);

  const tabs = el("div", "drawer__tabs");
  tabs.setAttribute("role", "tablist");
  const tabBtns = new Map<Tab, HTMLButtonElement>();
  for (const t of ["chats", "agents"] as Tab[]) {
    const b = el("button", "drawer__tab", t === "chats" ? "Chats" : "Agents");
    b.type = "button";
    b.setAttribute("role", "tab");
    b.addEventListener("click", () => setTab(t));
    tabs.appendChild(b);
    tabBtns.set(t, b);
  }
  // Hide tab bar in single-tab mode.
  if (!singleTab) root.appendChild(tabs);

  const chatsPage = el("div", "drawer__page drawer__page--chats");
  const agentsPage = el("div", "drawer__page drawer__page--agents");
  root.appendChild(chatsPage);
  root.appendChild(agentsPage);

  function convSecondary(conv: AtmosConv): string {
    if (conv.state === "running") return "Running…";
    if (conv.state === "failed") return "Failed";
    return conv.kind;
  }

  // Wave 35 — attention sort. Matches the bot board priority
  // (running > paused > idle > failed) so all three surfaces (TG
  // bot, mini app, Mac app) show the same row order. User does
  // not have to scan for "which tab is doing something".
  function convPriority(c: AtmosConv): number {
    const s = (c.state || "").toLowerCase();
    if (s === "running") return 0;
    if (s === "paused" || s === "blocked") return 1;
    if (s === "idle" || s === "done" || s === "") return 2;
    if (s === "failed") return 3;
    return 2;
  }

  function renderChats(): void {
    chatsPage.replaceChildren();
    const live = atmosStore.convs()
      .slice()
      .sort((a, b) => convPriority(a) - convPriority(b)
                       || (b.cid - a.cid));
    if (live.length === 0) {
      chatsPage.appendChild(
        emptyBlock(
          "No chats yet",
          "Tap + to start a chat. Mac will spawn an agent for you.",
        ),
      );
      syncActive();
      return;
    }
    const list = el("div", "drawer__list");
    for (const c of live) {
      list.appendChild(
        buildItem(
          String(c.cid), c.name, undefined, convSecondary(c),
          (id) => { onPick(id); syncActive(); },
        ),
      );
    }
    chatsPage.appendChild(list);
    syncActive();
  }

  function setFilter(next: RunFilter): void {
    filter = next;
    renderAgents();
  }

  function agentTagline(a: AtmosAgent): string {
    if (a.tagline) return a.tagline;
    if (a.model && a.tools && a.tools.length > 0) {
      return `${a.model} · ${a.tools.join(", ")}`;
    }
    return a.model ?? a.status ?? "agent";
  }

  function renderAgents(): void {
    agentsPage.replaceChildren();

    agentsPage.appendChild(el("div", "drawer__heading", "Roster"));
    // Wave 35 — attention sort (busy → online → idle → offline)
    // matches bot board + chat list. Same rule on every surface.
    const agentPriority = (s?: string): number => {
      const v = (s || "").toLowerCase();
      if (v === "busy") return 0;
      if (v === "online") return 1;
      if (v === "idle") return 2;
      if (v === "offline") return 3;
      return 2;
    };
    const liveAgents = atmosStore.agents()
      .filter((a) => !deletedAgents.has(a.id))
      .map<Agent>((a) => ({
        ...a,
        name: renamedAgents.get(a.id) ?? a.name,
        tagline: agentTagline(a),
      }))
      .sort((a, b) =>
        agentPriority(a.status) - agentPriority(b.status)
        || a.name.localeCompare(b.name));

    if (liveAgents.length === 0) {
      agentsPage.appendChild(
        emptyBlock(
          "No agents yet",
          "Tap + New agent to spin one up. Mac handles the rest.",
        ),
      );
    } else {
      const roster = el("div", "drawer__list");
      for (const a of liveAgents) {
        roster.appendChild(buildAgentRow(a, {
          onPick: (id) => { onPick(id); syncActive(); },
          onConfigure: (ag) => onAgentConfigure(ag),
          onRenameSubmit: (id, newName) => {
            renamedAgents.set(id, newName);
            atmosWS.renameAgent(id, newName);
          },
          onDuplicate: (ag) => {
            atmosWS.duplicateAgent(ag.id);
            // Mac will broadcast agent.list.r + conv.update add — the
            // store subscribers re-render this drawer + the chats tab
            // automatically. No local optimistic insert needed.
          },
          onDelete: (ag) => {
            deletedAgents.add(ag.id);
            atmosWS.deleteAgent(ag.id);
            renderAgents();
          },
        }));
      }
      agentsPage.appendChild(roster);
    }

    agentsPage.appendChild(el("div", "drawer__heading", "Activity"));

    const chips = el("div", "drawer__chips");
    for (const f of ["working", "completed"] as RunFilter[]) {
      const c = el("button", "drawer__chip", f === "working" ? "Working" : "Completed");
      c.type = "button";
      if (f === filter) c.classList.add("drawer__chip--active");
      c.addEventListener("click", () => setFilter(f));
      chips.appendChild(c);
    }
    agentsPage.appendChild(chips);

    const runs = atmosStore.runs().filter((r) =>
      filter === "working"
        ? r.status === "working" || r.status === "queued"
        : r.status === "done" || r.status === "failed",
    );

    if (runs.length === 0) {
      agentsPage.appendChild(
        el("p", "drawer__empty",
          filter === "working" ? "No agents running." : "Nothing finished yet."),
      );
      return;
    }

    const stack = el("div", "drawer__runs");
    for (const r of runs) stack.appendChild(buildRunCard(r));
    agentsPage.appendChild(stack);
  }

  function setTab(next: Tab): void {
    tab = next;
    for (const [k, b] of tabBtns) {
      b.classList.toggle("drawer__tab--active", k === next);
      b.setAttribute("aria-selected", k === next ? "true" : "false");
    }
    chatsPage.classList.toggle("drawer__page--visible", next === "chats");
    agentsPage.classList.toggle("drawer__page--visible", next === "agents");
    ctaLabel.textContent = next === "chats" ? "New chat" : "New agent";
    if (next === "agents") renderAgents();
  }

  renderChats();
  atmosStore.onConvs(renderChats);
  atmosStore.onAgents(renderAgents);
  atmosStore.onRuns(renderAgents);
  atmosStore.onLink(() => { renderChats(); renderAgents(); });
  setTab(singleTab ?? "chats");

  (root as unknown as { setTab: (t: Tab) => void }).setTab = setTab;

  return root;
}

export default Drawer;
